import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Edit3, 
  Save, 
  RefreshCw,
  Eye,
  CheckCircle,
  AlertCircle,
  Star,
  X,
  Upload,
  FolderPlus,
  File
} from 'lucide-react';
import V6System from './V6System';

const V5BatchSystem = () => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('v6');
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [modalActiveTab, setModalActiveTab] = useState('summary');
  const [editingScript, setEditingScript] = useState(false);
  const [scriptContent, setScriptContent] = useState('');
  
  // Sandbox state
  const [sandboxPrompt, setSandboxPrompt] = useState('');
  const [sandboxArticleId, setSandboxArticleId] = useState('');
  const [sandboxOutput, setSandboxOutput] = useState('');
  const [sandboxLoading, setSandboxLoading] = useState(false);

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [fileName, setFileName] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [extractionComplete, setExtractionComplete] = useState(false);
  const [extractedDocumentId, setExtractedDocumentId] = useState(null);

  // Function to extract narrator's text from script JSON for ElevenLabs
  const extractNarratorText = (scriptJson) => {
    if (!scriptJson || !scriptJson.beats) return '';
    
    let narratorText = '';
    
    // Add hook if it exists
    if (scriptJson.hook) {
      narratorText += scriptJson.hook + '\n\n';
    }
    
    // Add beats
    scriptJson.beats.forEach((beat, index) => {
      if (typeof beat === 'string') {
        narratorText += beat + '\n\n';
      } else if (beat.voiceover) {
        narratorText += beat.voiceover + '\n\n';
      }
    });
    
    // Add CTA if it exists
    if (scriptJson.cta) {
      narratorText += scriptJson.cta;
    }
    
    return narratorText.trim();
  };

  // Fetch documents
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v5/documents?limit=1000`);
      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group documents by folder/issue
  const groupDocumentsByFolder = () => {
    const grouped = {};
    documents.forEach(doc => {
      const folderKey = `${doc.issue} - ${doc.source_meta?.month_year || 'Unknown'}`;
      if (!grouped[folderKey]) {
        grouped[folderKey] = {
          issue: doc.issue,
          monthYear: doc.source_meta?.month_year || 'Unknown',
          year: doc.year,
          documents: []
        };
      }
      grouped[folderKey].documents.push(doc);
    });
    return grouped;
  };

  // Toggle folder expansion
  const toggleFolder = (folderKey) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderKey)) {
      newExpanded.delete(folderKey);
    } else {
      newExpanded.add(folderKey);
    }
    setExpandedFolders(newExpanded);
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v5/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Process document
  const processDocument = async (documentId, steps) => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v5/documents/${documentId}/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ steps })
      });
      
      if (response.ok) {
        await fetchDocuments();
        await fetchStats();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error processing document:', error);
      alert('Error processing document');
    } finally {
      setLoading(false);
    }
  };

  // Load document detail
  const loadDocumentDetail = async (documentId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v5/documents/${documentId}`);
      const data = await response.json();
      console.log('Loaded document detail:', data); // Debug log
      console.log('Document summary:', data.summary);
      console.log('Document analysis:', data.analysis);
      console.log('Document script:', data.script);
      setSelectedDocument(data);
      setShowModal(true);
      setModalActiveTab('summary');
      setEditingScript(false);
      setScriptContent(extractNarratorText(data.script?.script_json) || data.script?.script_text || '');
    } catch (error) {
      console.error('Error loading document detail:', error);
    }
  };

  // Save script
  const saveScript = async () => {
    if (!selectedDocument) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v5/scripts/${selectedDocument.script.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ script_text: scriptContent })
      });
      
      if (response.ok) {
        setEditingScript(false);
        await loadDocumentDetail(selectedDocument.id);
        await fetchDocuments();
      } else {
        alert('Error saving script');
      }
    } catch (error) {
      console.error('Error saving script:', error);
      alert('Error saving script');
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchStats();
    loadSandboxPrompt();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-gray-100 text-gray-800';
      case 'summarized': return 'bg-blue-100 text-blue-800';
      case 'scored': return 'bg-yellow-100 text-yellow-800';
      case 'ready_for_script': return 'bg-green-100 text-green-800';
      case 'script_generated': return 'bg-purple-100 text-purple-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };


  // Sandbox functions
  const loadSandboxPrompt = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v5/sandbox/prompt`);
      const data = await response.json();
      if (data.success) {
        setSandboxPrompt(data.prompt || '');
      }
    } catch (error) {
      console.error('Error loading sandbox prompt:', error);
    }
  };

  const saveSandboxPrompt = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v5/sandbox/prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: sandboxPrompt }),
      });
      const data = await response.json();
      if (data.success) {
        console.log('Sandbox prompt saved');
      }
    } catch (error) {
      console.error('Error saving sandbox prompt:', error);
    }
  };

  const generateSandboxScript = async () => {
    if (!sandboxArticleId || !sandboxPrompt.trim()) {
      alert('Please enter both an article ID and a prompt');
      return;
    }

    setSandboxLoading(true);
    setSandboxOutput('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v5/sandbox/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          article_id: parseInt(sandboxArticleId),
          prompt: sandboxPrompt,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSandboxOutput(data.script || 'No script generated');
      } else {
        setSandboxOutput(`Error: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      setSandboxOutput(`Error: ${error.message}`);
    } finally {
      setSandboxLoading(false);
    }
  };

  // Upload functions
  const fetchAvailableFolders = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/upload/folders`);
      const data = await response.json();
      if (data.success) {
        setAvailableFolders(data.folders || []);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadFile(file);
      setFileName(file.name);
    } else {
      alert('Please select a PDF file');
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadFile(file);
      setFileName(file.name);
    } else {
      alert('Please select a PDF file');
    }
  };

  const uploadToSupabase = async () => {
    if (!uploadFile || !fileName) {
      alert('Please select a file and provide a filename');
      return;
    }

    const folderPath = showNewFolderInput ? newFolderName : selectedFolder;
    if (!folderPath) {
      alert('Please select or create a folder');
      return;
    }

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('folder', folderPath);
      formData.append('filename', fileName);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/upload/pdf`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setUploadedDocument(data.document);
        alert('File uploaded successfully!');
        // Reset form
        setUploadFile(null);
        setFileName('');
        setSelectedFolder('');
        setNewFolderName('');
        setShowNewFolderInput(false);
      } else {
        alert(`Upload failed: ${data.message}`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  const extractPDFContent = async (documentId) => {
    setUploadLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/upload/extract/${documentId}`, {
        method: 'POST'
      });

      const data = await response.json();
      if (data.success) {
        alert('PDF content extracted successfully!');
        setExtractionComplete(true);
        setExtractedDocumentId(documentId);
        setUploadedDocument(null); // Clear the uploaded document
      } else {
        alert(`Extraction failed: ${data.message}`);
      }
    } catch (error) {
      console.error('Error extracting PDF:', error);
      alert('Extraction failed');
    } finally {
      setUploadLoading(false);
    }
  };

  const generateScripts = async (documentId) => {
    setUploadLoading(true);
    try {
      // Use the existing regenerate-scripts endpoint that already works
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/documents/${documentId}/regenerate-scripts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate scripts');
      }

      const data = await response.json();
      alert(`Successfully generated ${data.scripts_count || 5} scripts using enhanced prompt ID=6 with beats integration!`);
      setExtractionComplete(false);
      setExtractedDocumentId(null);
      
    } catch (error) {
      console.error('Error generating scripts:', error);
      alert(`Script generation failed: ${error.message}`);
    } finally {
      setUploadLoading(false);
    }
  };

  // Load folders when upload tab is selected
  useEffect(() => {
    if (activeTab === 'upload') {
      fetchAvailableFolders();
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <FileText className="h-8 w-8 text-purple-600" />
              <h1 className="text-2xl font-bold text-gray-900">V5 Batch Script System</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setActiveTab('v6')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  activeTab === 'v6' 
                    ? 'bg-purple-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                LIBRARY
              </button>
              <button
                onClick={() => setActiveTab('upload')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  activeTab === 'upload' 
                    ? 'bg-purple-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Upload className="h-4 w-4 mr-2 inline" />
                Upload
              </button>
              <button
                onClick={() => setActiveTab('sandbox')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  activeTab === 'sandbox' 
                    ? 'bg-purple-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Sandbox
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {false && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Document Library</h2>
              <button
                onClick={fetchDocuments}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {/* Folder Structure */}
            <div className="bg-white rounded-lg shadow">
              {Object.entries(groupDocumentsByFolder()).map(([folderKey, folderData]) => (
                <div key={folderKey} className="border-b border-gray-200 last:border-b-0">
                  <div 
                    className="px-4 py-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleFolder(folderKey)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="mr-3">
                          {expandedFolders.has(folderKey) ? (
                            <div className="w-4 h-4 text-gray-500">▼</div>
                          ) : (
                            <div className="w-4 h-4 text-gray-500">▶</div>
                          )}
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">{folderData.issue}</h3>
                          <p className="text-sm text-gray-500">{folderData.monthYear}</p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500">
                        {folderData.documents.length} documents
                      </span>
                    </div>
                  </div>
                  
                  {expandedFolders.has(folderKey) && (
                    <div className="divide-y divide-gray-200">
                      {folderData.documents.map((doc) => (
                        <div key={doc.id} className="px-4 py-3 hover:bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-gray-900">{doc.title}</h5>
                                  <p className="text-xs text-gray-500">
                                    {doc.source_meta?.author || 'Unknown Author'} • {new Date(doc.updated_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="ml-4 flex items-center space-x-2">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(doc.status)}`}>
                                    {doc.status.replace('_', ' ')}
                                  </span>
                                  {doc.analysis && (
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                      Score: {doc.extraction?.quality_score || doc.analysis.recommendation_score || 'N/A'}
                                    </span>
                                  )}
                                  {doc.extraction?.case_study_detected && (
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                      📊 Case Study
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="ml-4 flex space-x-2">
                              <button
                                onClick={() => loadDocumentDetail(doc.id)}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => processDocument(doc.id, ['extract', 'summarize', 'evaluate', 'script'])}
                                disabled={loading}
                                className="text-green-600 hover:text-green-900 disabled:opacity-50 p-1"
                                title="Process Document"
                              >
                                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {false && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Documents</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.total_documents}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Scripts Generated</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.status_counts.script_generated || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <Star className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Average Score</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.average_score}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <AlertCircle className="h-8 w-8 text-red-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Errors</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.status_counts.error || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sandbox' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Prompt Sandbox</h2>
              <div className="flex space-x-2">
                <button
                  onClick={loadSandboxPrompt}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Load Saved Prompt
                </button>
                <button
                  onClick={saveSandboxPrompt}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Prompt
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input Section */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Article ID
                  </label>
                  <input
                    type="number"
                    value={sandboxArticleId}
                    onChange={(e) => setSandboxArticleId(e.target.value)}
                    placeholder="Enter article ID (e.g., 157)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Prompt
                  </label>
                  <textarea
                    value={sandboxPrompt}
                    onChange={(e) => setSandboxPrompt(e.target.value)}
                    placeholder="Enter your custom prompt here..."
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <button
                  onClick={generateSandboxScript}
                  disabled={sandboxLoading || !sandboxArticleId || !sandboxPrompt.trim()}
                  className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sandboxLoading ? 'Generating...' : 'Generate Script'}
                </button>
              </div>

              {/* Output Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Generated Script
                </label>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 h-96 overflow-y-auto">
                  {sandboxOutput ? (
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                      {sandboxOutput}
                    </pre>
                  ) : (
                    <p className="text-gray-500 italic">
                      {sandboxLoading ? 'Generating script...' : 'Generated script will appear here'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">How to use the Sandbox:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Enter an article ID from your library (e.g., 157)</li>
                <li>• Write or modify your custom prompt in the text area</li>
                <li>• Click "Generate Script" to test your prompt</li>
                <li>• Use "Save Prompt" to store your prompt for future use</li>
                <li>• Use "Load Saved Prompt" to retrieve your saved prompt</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{selectedDocument.title}</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Case Study Notification */}
              {selectedDocument.extraction?.case_study_detected && (
                <div className="mb-4 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Case Study Present
                </div>
              )}

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex space-x-8">
                  {['summary', 'analysis', 'script'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setModalActiveTab(tab)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        modalActiveTab === tab
                          ? 'border-purple-500 text-purple-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="max-h-96 overflow-y-auto">
                {modalActiveTab === 'summary' && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Summary</h4>
                    {(() => {
                      console.log('Summary tab - selectedDocument:', selectedDocument);
                      console.log('Summary tab - summary:', selectedDocument?.summary);
                      return null;
                    })()}
                    {selectedDocument?.summary ? (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-sm text-gray-700 leading-relaxed">
                          {selectedDocument.summary.summary_json?.summary || selectedDocument.summary.summary_text || 'No summary content available'}
                        </div>
                        {selectedDocument.summary.summary_json?.key_points && selectedDocument.summary.summary_json.key_points.length > 0 && (
                          <div className="mt-4">
                            <h5 className="font-medium text-gray-900 mb-2">Key Points:</h5>
                            <ul className="list-disc list-inside text-sm text-gray-600">
                              {selectedDocument.summary.summary_json.key_points.map((point, index) => (
                                <li key={index}>{point}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {selectedDocument.summary.summary_json?.word_count && (
                          <div className="mt-2 text-xs text-gray-500">
                            Word count: {selectedDocument.summary.summary_json.word_count}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                        <p className="text-sm text-yellow-700">No summary available. Click "Process Document" to generate one.</p>
                      </div>
                    )}
                  </div>
                )}

                {modalActiveTab === 'analysis' && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Analysis</h4>
                    {(() => {
                      console.log('Analysis tab - selectedDocument:', selectedDocument);
                      console.log('Analysis tab - analysis:', selectedDocument?.analysis);
                      return null;
                    })()}
                    {selectedDocument?.analysis ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h5 className="font-medium text-gray-900 mb-2">Score: {selectedDocument.extraction?.quality_score || selectedDocument.analysis.recommendation_score || 'N/A'}</h5>
                            <p className="text-sm text-gray-600">{selectedDocument.analysis.evaluation_json?.rationale || 'No rationale available'}</p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h5 className="font-medium text-gray-900 mb-2">Verdict: {selectedDocument.analysis.evaluation_json?.verdict || 'N/A'}</h5>
                            <p className="text-sm text-gray-600">{selectedDocument.analysis.evaluation_json?.one_liner || 'No one-liner available'}</p>
                          </div>
                        </div>
                        
                        {selectedDocument.analysis.evaluation_json?.strengths && selectedDocument.analysis.evaluation_json.strengths.length > 0 && (
                          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                            <h5 className="font-medium text-green-900 mb-2">Strengths:</h5>
                            <ul className="text-sm text-green-700 list-disc list-inside">
                              {selectedDocument.analysis.evaluation_json.strengths.map((strength, index) => (
                                <li key={index}>{strength}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {selectedDocument.analysis.evaluation_json?.weaknesses && selectedDocument.analysis.evaluation_json.weaknesses.length > 0 && (
                          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                            <h5 className="font-medium text-red-900 mb-2">Weaknesses:</h5>
                            <ul className="text-sm text-red-700 list-disc list-inside">
                              {selectedDocument.analysis.evaluation_json.weaknesses.map((weakness, index) => (
                                <li key={index}>{weakness}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {selectedDocument.analysis.evaluation_json?.detailed_scores && (
                          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <h5 className="font-medium text-blue-900 mb-2">Detailed Scores:</h5>
                            <div className="grid grid-cols-2 gap-2 text-sm text-blue-700">
                              {Object.entries(selectedDocument.analysis.evaluation_json.detailed_scores).map(([key, value]) => (
                                <div key={key} className="flex justify-between">
                                  <span className="capitalize">{key.replace('_', ' ')}:</span>
                                  <span className="font-medium">{value}/5</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                        <p className="text-sm text-yellow-700">No analysis available. Click "Process Document" to generate one.</p>
                      </div>
                    )}
                  </div>
                )}

                {modalActiveTab === 'script' && (
                  <div className="space-y-4">
                    {(() => {
                      console.log('Script tab - selectedDocument:', selectedDocument);
                      console.log('Script tab - script:', selectedDocument?.script);
                      return null;
                    })()}
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold text-gray-900">Script</h4>
                        <p className="text-sm text-gray-500">Ready for ElevenLabs voice generation</p>
                      </div>
                      <div className="flex space-x-2">
                        {!selectedDocument.script && (
                          <button
                            onClick={() => processDocument(selectedDocument.id, ['script'])}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                          >
                            Generate Script
                          </button>
                        )}
                        {selectedDocument.script && (
                          <>
                            <button
                              onClick={() => setEditingScript(!editingScript)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                              <Edit3 className="h-4 w-4 inline mr-2" />
                              {editingScript ? 'Cancel' : 'Edit'}
                            </button>
                            {editingScript && (
                              <button
                                onClick={saveScript}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                              >
                                <Save className="h-4 w-4 inline mr-2" />
                                Save
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    
                    {selectedDocument.script ? (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg">
                        {editingScript ? (
                          <textarea
                            value={scriptContent}
                            onChange={(e) => setScriptContent(e.target.value)}
                            className="w-full h-64 p-4 border-0 rounded-lg text-sm leading-relaxed"
                            placeholder="Enter your script text here..."
                          />
                        ) : (
                          <div className="p-4">
                            <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                              {extractNarratorText(selectedDocument.script.script_json) || selectedDocument.script.script_text || 'No script content available'}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                        <p className="text-sm text-yellow-700">No script available. Click "Generate Script" to create one.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Tab Content */}
      {activeTab === 'upload' && (
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Manual PDF Upload</h2>
            
            {/* Step 1: File Upload */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">1. Select PDF File</h3>
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {uploadFile ? (
                  <div className="flex items-center justify-center space-x-2">
                    <File className="h-8 w-8 text-purple-600" />
                    <span className="text-lg font-medium text-gray-900">{uploadFile.name}</span>
                    <button
                      onClick={() => setUploadFile(null)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-900 mb-2">Drop PDF file here</p>
                    <p className="text-sm text-gray-500 mb-4">or</p>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="pdf-upload"
                    />
                    <label
                      htmlFor="pdf-upload"
                      className="cursor-pointer bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Browse Files
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: Folder Selection */}
            {uploadFile && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">2. Select Destination Folder</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Choose existing folder:
                    </label>
                    <select
                      value={selectedFolder}
                      onChange={(e) => {
                        setSelectedFolder(e.target.value);
                        setShowNewFolderInput(false);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                      disabled={showNewFolderInput}
                    >
                      <option value="">Select a folder...</option>
                      {availableFolders.map((folder) => (
                        <option key={folder} value={folder}>{folder}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="text-center">
                    <span className="text-sm text-gray-500">or</span>
                  </div>
                  
                  <div>
                    <button
                      onClick={() => {
                        setShowNewFolderInput(!showNewFolderInput);
                        setSelectedFolder('');
                      }}
                      className="flex items-center space-x-2 text-purple-600 hover:text-purple-700 font-medium"
                    >
                      <FolderPlus className="h-5 w-5" />
                      <span>Create New Folder</span>
                    </button>
                    
                    {showNewFolderInput && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New folder name (e.g., "YPN207 - September 2025"):
                        </label>
                        <input
                          type="text"
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          placeholder="YPN207 - September 2025"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Filename */}
            {uploadFile && (selectedFolder || newFolderName) && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">3. Set Filename</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filename (e.g., "SimonZutshi-YPN206.pdf"):
                  </label>
                  <input
                    type="text"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    placeholder="SimonZutshi-YPN206.pdf"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>
            )}

            {/* Upload Button */}
            {uploadFile && (selectedFolder || newFolderName) && fileName && (
              <div className="mb-8">
                <button
                  onClick={uploadToSupabase}
                  disabled={uploadLoading}
                  className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploadLoading ? 'Uploading...' : 'Upload to Supabase'}
                </button>
              </div>
            )}

            {/* Step 4: Extract Content */}
            {uploadedDocument && (
              <div className="border-t pt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">4. Extract PDF Content</h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-800">File uploaded successfully!</span>
                  </div>
                  <p className="text-sm text-green-700">
                    Document ID: {uploadedDocument.id} | Path: {uploadedDocument.file_path}
                  </p>
                </div>
                <button
                  onClick={() => extractPDFContent(uploadedDocument.id)}
                  disabled={uploadLoading}
                  className="bg-blue-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploadLoading ? 'Extracting...' : 'Extract PDF Content'}
                </button>
              </div>
            )}

            {/* Script Generation Section - appears after extraction is complete */}
            {extractionComplete && extractedDocumentId && (
              <div className="border-t pt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">5. Generate Scripts</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-blue-800">Content extracted successfully!</span>
                  </div>
                  <p className="text-sm text-blue-700">
                    Ready to generate 5 scripts using enhanced prompt ID=6 with beats integration
                  </p>
                </div>
                <button
                  onClick={() => generateScripts(extractedDocumentId)}
                  disabled={uploadLoading}
                  className="bg-green-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploadLoading ? 'Generating Scripts...' : 'Generate 5 Scripts (Prompt ID=6)'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* V6 Tab Content */}
      {activeTab === 'v6' && (
        <V6System />
      )}
    </div>
  );
};

export default V5BatchSystem;